import type { Vec2 } from './Vec2';
import { vec2 } from './Vec2';
import type { Segment } from './Terrain';
import type { Random } from './Random';
import { GAME_WIDTH, GAME_HEIGHT } from '../constants';
import { pointToSegmentDist } from './LevelGenerator';

/** Split segments that cross the wrap boundary (|a.x - b.x| > GAME_WIDTH/2).
 *  Interpolates Y at the boundary to create two segments. */
function splitAtWrapBoundary(segments: Segment[]): Segment[] {
  const result: Segment[] = [];
  const halfW = GAME_WIDTH / 2;

  for (const seg of segments) {
    const dx = seg.b.x - seg.a.x;
    if (Math.abs(dx) > halfW) {
      if (dx > 0) {
        const wrappedDx = GAME_WIDTH - dx;
        const t = seg.a.x / wrappedDx;
        const boundaryY = seg.a.y + (seg.b.y - seg.a.y) * t;
        result.push({ a: seg.a, b: vec2(0, boundaryY) });
        result.push({ a: vec2(GAME_WIDTH, boundaryY), b: seg.b });
      } else {
        const wrappedDx = GAME_WIDTH + dx;
        const t = (GAME_WIDTH - seg.a.x) / wrappedDx;
        const boundaryY = seg.a.y + (seg.b.y - seg.a.y) * t;
        result.push({ a: seg.a, b: vec2(GAME_WIDTH, boundaryY) });
        result.push({ a: vec2(0, boundaryY), b: seg.b });
      }
    } else {
      result.push(seg);
    }
  }

  return result;
}

/** For a structural piece, determine its left (min-X) and right (max-X) endpoints. */
function getLeftRight(piece: { a: Vec2; b: Vec2 }): { left: Vec2; right: Vec2 } {
  if (piece.a.x <= piece.b.x) {
    return { left: piece.a, right: piece.b };
  }
  return { left: piece.b, right: piece.a };
}

/** Determine ordered connection endpoints for all structural segments.
 *  Segments are ordered: [pad, edge1, edge2, ..., cupFloor, cupLeft, cupRight].
 *
 *  Structural pieces are sorted by X position (left to right). Each connection
 *  goes from the rightmost point of one piece to the leftmost point of the next.
 *  The final connection wraps from the rightmost of the last piece back to the
 *  leftmost of the first piece (going rightward off-screen). */
function getConnectionChain(
  segments: Segment[],
  numEdges: number,
): { from: Vec2; to: Vec2; wrap: boolean }[] {
  const numStructural = segments.length;
  if (numStructural < 4) return []; // Need at least pad + cup

  const cupStart = 1 + numEdges;

  const pieces: { left: Vec2; right: Vec2 }[] = [];

  // Tee pad
  pieces.push(getLeftRight(segments[0]!));

  // Edges
  for (let i = 1; i <= numEdges; i++) {
    pieces.push(getLeftRight(segments[i]!));
  }

  // Cup: left endpoint is left wall top, right endpoint is right wall top
  const cupLeftWall = segments[cupStart + 1]!;
  const cupRightWall = segments[cupStart + 2]!;
  const cupLeftTop = cupLeftWall.a;
  const cupRightTop = cupRightWall.a;
  if (cupLeftTop.x <= cupRightTop.x) {
    pieces.push({ left: cupLeftTop, right: cupRightTop });
  } else {
    pieces.push({ left: cupRightTop, right: cupLeftTop });
  }

  // Sort pieces by leftmost X coordinate
  pieces.sort((a, b) => a.left.x - b.left.x);

  // Build connections: rightmost of piece[i] → leftmost of piece[i+1]
  const connections: { from: Vec2; to: Vec2; wrap: boolean }[] = [];
  for (let i = 0; i < pieces.length - 1; i++) {
    connections.push({
      from: pieces[i]!.right,
      to: pieces[i + 1]!.left,
      wrap: false,
    });
  }

  // Final wrap: rightmost of last piece → leftmost of first piece
  connections.push({
    from: pieces[pieces.length - 1]!.right,
    to: pieces[0]!.left,
    wrap: true,
  });

  return connections;
}

/** Generate a straight-line connection between two points.
 *  For wrap connections, produces two segments: one going right to the screen edge,
 *  and one coming in from the left edge. */
function generateConnection(
  conn: { from: Vec2; to: Vec2; wrap: boolean },
): Segment[] {
  if (!conn.wrap) {
    return [{ a: conn.from, b: conn.to }];
  }

  // Wrap: go rightward from 'from' off the right edge, re-enter from the left to 'to'
  const rightDist = GAME_WIDTH - conn.from.x; // distance from 'from' to right edge
  const leftDist = conn.to.x;                 // distance from left edge to 'to'
  const totalDist = rightDist + leftDist;
  const t = totalDist > 0 ? rightDist / totalDist : 0.5;
  const boundaryY = conn.from.y + t * (conn.to.y - conn.from.y);

  return [
    { a: conn.from, b: vec2(GAME_WIDTH, boundaryY) },
    { a: vec2(0, boundaryY), b: conn.to },
  ];
}

/** Connect structural terrain segments with straight lines.
 *  Returns only the NEW connecting segments (not the original structural ones). */
export function connectSegments(
  segments: Segment[],
  numEdges: number,
  _trajectory: Vec2[],
  _rng: Random,
): Segment[] {
  const connections = getConnectionChain(segments, numEdges);
  const allConnectingSegments: Segment[] = [];

  for (const conn of connections) {
    allConnectingSegments.push(...generateConnection(conn));
  }

  return allConnectingSegments;
}

/** Like connectSegments but returns per-connection segment groups for debug visualization. */
export function connectSegmentsDebug(
  segments: Segment[],
  numEdges: number,
  _trajectory: Vec2[],
  _rng: Random,
): Segment[][] {
  const connections = getConnectionChain(segments, numEdges);
  const result: Segment[][] = [];

  for (const conn of connections) {
    result.push(generateConnection(conn));
  }

  return result;
}

const SUBDIV_MIN_LENGTH = 20;
const SUBDIV_CLEARANCE = 20;
const SUBDIV_MAX_SEGMENTS = 1000;
const SCREEN_MARGIN = 20;
const NORMAL_STEP = 2;
const MAX_WALK_DIST = 60;
const DISPLACEMENT_SCALE = 0.3;

/** Check if a point is within SUBDIV_CLEARANCE of any segment in the list. */
function isPointTooClose(px: number, py: number, segments: Segment[]): boolean {
  const p = vec2(px, py);
  for (let i = 0; i < segments.length; i++) {
    if (pointToSegmentDist(p, segments[i]!) < SUBDIV_CLEARANCE) return true;
  }
  return false;
}

/** Filter segments to only those whose bounding box overlaps a region. */
function filterNearby(segments: Segment[], cx: number, cy: number, radius: number): Segment[] {
  const result: Segment[] = [];
  for (let i = 0; i < segments.length; i++) {
    const s = segments[i]!;
    const minX = Math.min(s.a.x, s.b.x) - radius;
    const maxX = Math.max(s.a.x, s.b.x) + radius;
    const minY = Math.min(s.a.y, s.b.y) - radius;
    const maxY = Math.max(s.a.y, s.b.y) + radius;
    if (cx >= minX && cx <= maxX && cy >= minY && cy <= maxY) {
      result.push(s);
    }
  }
  return result;
}

/** Iteratively subdivide connecting segments using midpoint displacement.
 *  Picks a random long segment, displaces its midpoint along the normal
 *  within a clearance-constrained range, and splits into two segments.
 *  Stops when all segments are ≤ SUBDIV_MIN_LENGTH or count ≥ SUBDIV_MAX_SEGMENTS. */
export function subdivideConnections(
  connections: Segment[],
  structuralSegments: Segment[],
  trajectory: Vec2[],
  rng: Random,
): Segment[] {
  const segments = [...connections];

  // Pre-build trajectory segments once, filtering out wrap crossings
  const halfW = GAME_WIDTH / 2;
  const trajSegments: Segment[] = [];
  for (let i = 0; i < trajectory.length - 1; i++) {
    const ta = trajectory[i]!;
    const tb = trajectory[i + 1]!;
    if (Math.abs(tb.x - ta.x) > halfW) continue;
    trajSegments.push({ a: ta, b: tb });
  }

  let consecutiveSkips = 0;
  const MAX_SKIPS = 20;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (segments.length >= SUBDIV_MAX_SEGMENTS) break;
    if (consecutiveSkips >= MAX_SKIPS) break;

    // Build candidate list: segments longer than minimum length
    const candidates: number[] = [];
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i]!;
      const sdx = seg.b.x - seg.a.x;
      const sdy = seg.b.y - seg.a.y;
      if (sdx * sdx + sdy * sdy > SUBDIV_MIN_LENGTH * SUBDIV_MIN_LENGTH) {
        candidates.push(i);
      }
    }

    if (candidates.length === 0) break;

    // Pick a random candidate
    const ci = candidates[rng.int(0, candidates.length - 1)]!;
    const seg = segments[ci]!;

    // Compute midpoint
    const midX = (seg.a.x + seg.b.x) / 2;
    const midY = (seg.a.y + seg.b.y) / 2;

    // Compute unit normal (perpendicular to segment direction)
    const dx = seg.b.x - seg.a.x;
    const dy = seg.b.y - seg.a.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const nx = -dy / len;
    const ny = dx / len;

    // Filter to only nearby segments for clearance checking
    const searchRadius = MAX_WALK_DIST + SUBDIV_CLEARANCE;
    const nearTraj = filterNearby(trajSegments, midX, midY, searchRadius);
    const nearStructural = filterNearby(structuralSegments, midX, midY, searchRadius);

    // Build nearby clearance segments: structural + connections except the one being split
    const nearClearance: Segment[] = [...nearStructural];
    for (let i = 0; i < segments.length; i++) {
      if (i === ci) continue;
      const s = segments[i]!;
      const sMinX = Math.min(s.a.x, s.b.x) - searchRadius;
      const sMaxX = Math.max(s.a.x, s.b.x) + searchRadius;
      const sMinY = Math.min(s.a.y, s.b.y) - searchRadius;
      const sMaxY = Math.max(s.a.y, s.b.y) + searchRadius;
      if (midX >= sMinX && midX <= sMaxX && midY >= sMinY && midY <= sMaxY) {
        nearClearance.push(s);
      }
    }

    // Walk along +normal to find max positive extent
    let maxPos = 0;
    for (let d = NORMAL_STEP; d <= MAX_WALK_DIST; d += NORMAL_STEP) {
      const px = midX + nx * d;
      const py = midY + ny * d;
      if (px < 0 || px > GAME_WIDTH) break;
      if (py < SCREEN_MARGIN || py > GAME_HEIGHT - SCREEN_MARGIN) break;
      if (isPointTooClose(px, py, nearTraj) || isPointTooClose(px, py, nearClearance)) break;
      maxPos = d;
    }

    // Walk along -normal to find max negative extent
    let maxNeg = 0;
    for (let d = NORMAL_STEP; d <= MAX_WALK_DIST; d += NORMAL_STEP) {
      const px = midX - nx * d;
      const py = midY - ny * d;
      if (px < 0 || px > GAME_WIDTH) break;
      if (py < SCREEN_MARGIN || py > GAME_HEIGHT - SCREEN_MARGIN) break;
      if (isPointTooClose(px, py, nearTraj) || isPointTooClose(px, py, nearClearance)) break;
      maxNeg = d;
    }

    // If total valid range too small, skip this candidate
    if (maxPos + maxNeg < 4) {
      consecutiveSkips++;
      continue;
    }
    consecutiveSkips = 0;

    // Scale displacement range by segment length — longer segments get more
    // displacement, shorter ones get less, producing smoother curves
    const maxDisp = len * DISPLACEMENT_SCALE;
    const clampedPos = Math.min(maxPos, maxDisp);
    const clampedNeg = Math.min(maxNeg, maxDisp);

    // Pick random displacement within length-scaled range
    const displacement = rng.float(-clampedNeg, clampedPos);

    // Compute displaced midpoint
    const newMidX = midX + nx * displacement;
    const newMidY = midY + ny * displacement;
    const newMid = vec2(newMidX, newMidY);

    // Replace original segment with two new ones
    segments.splice(ci, 1, { a: seg.a, b: newMid }, { a: newMid, b: seg.b });
  }

  return segments;
}

// Export for testing
export { splitAtWrapBoundary, getConnectionChain };
